/**
 * Aba "Pneus": a lista, no padrão das tabelas do SIAG. Filtros na URL (dá para
 * mandar o link filtrado), paginação no servidor, situação em botões com
 * contagem. A situação e a posição vêm creditadas (SIAG × integração).
 */
import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import type { TireListDto, TireSituationDto, TireViewDto } from "@bi/types";
import { api } from "../../../lib/api";
import { EmptyState } from "../../../components/EmptyState";
import { IconTire } from "../../../components/icons";
import type { MoveTirePreset } from "../MoveTireModal";
import { InspectTireModal } from "../InspectTireModal";
import { ConditionBadge, fmtKm, fmtMm, OriginBadge, TabBar, TireStatusBadge, useTireCapabilities } from "../tire-ui";

const SITUACOES: Array<{ key: TireSituationDto; label: string }> = [
  { key: "all", label: "Todos" },
  { key: "installed", label: "Instalados" },
  { key: "stock", label: "Estoque" },
  { key: "retread", label: "Recapagem" },
  { key: "critical", label: "Críticos" },
  { key: "attention", label: "Atenção" },
  { key: "written_off", label: "Descartados" },
  { key: "unknown", label: "Não localizados" },
];

export function TireListTab({ onMove }: { onMove: (p: MoveTirePreset) => void }) {
  const [params, setParams] = useSearchParams();
  const situation = (params.get("situacao") as TireSituationDto) || "all";
  const q = params.get("q") ?? "";
  const brand = params.get("marca") ?? "";
  const plate = params.get("placa") ?? "";
  const life = params.get("vida") ?? "";
  const page = Number(params.get("pagina") ?? "1") || 1;

  const set = (chave: string, valor: string) => {
    const p = new URLSearchParams(params);
    if (valor) p.set(chave, valor); else p.delete(chave);
    if (chave !== "pagina") p.delete("pagina");
    setParams(p, { replace: true });
  };

  const lista = useQuery({
    queryKey: ["tires", "list", situation, q, brand, plate, life, page],
    queryFn: () => {
      const p = new URLSearchParams({ situation, page: String(page), pageSize: "25" });
      if (q) p.set("q", q);
      if (brand) p.set("brand", brand);
      if (plate) p.set("plate", plate);
      if (life) p.set("life", life);
      return api.get<TireListDto>(`/fleet/tires?${p}`);
    },
    placeholderData: (prev) => prev,
  });
  const d = lista.data;
  const paginas = d ? Math.max(1, Math.ceil(d.total / d.pageSize)) : 1;

  return (
    <div className="space-y-4">
      <TabBar<TireSituationDto>
        tabs={SITUACOES.map((s) => ({ ...s, count: d?.counts[s.key] ?? null }))}
        active={situation}
        onChange={(s) => set("situacao", s === "all" ? "" : s)}
      />

      <div className="card p-3 flex flex-wrap gap-2 items-center">
        <input className="input w-full sm:w-72" placeholder="Buscar por nº de fogo, código, marca, medida…" defaultValue={q}
          onKeyDown={(e) => { if (e.key === "Enter") set("q", (e.target as HTMLInputElement).value.trim()); }}
          onBlur={(e) => set("q", e.target.value.trim())} />
        <select className="input w-40" value={brand} onChange={(e) => set("marca", e.target.value)}>
          <option value="">Toda marca</option>
          {(d?.facets.brands ?? []).map((b) => <option key={b} value={b}>{b}</option>)}
        </select>
        <input className="input w-32 uppercase font-mono" placeholder="Veículo" defaultValue={plate}
          onKeyDown={(e) => { if (e.key === "Enter") set("placa", (e.target as HTMLInputElement).value.trim().toUpperCase()); }}
          onBlur={(e) => set("placa", e.target.value.trim().toUpperCase())} />
        <select className="input w-32" value={life} onChange={(e) => set("vida", e.target.value)}>
          <option value="">Toda vida</option>
          {(d?.facets.lives ?? ["Novo", "R1", "R2", "R3+"]).map((l) => <option key={l} value={l}>{l}</option>)}
        </select>
      </div>

      <div className="card overflow-x-auto">
        {lista.isLoading ? (
          <p className="p-6 text-sm text-gray-500">Carregando…</p>
        ) : lista.isError ? (
          <EmptyState icon={<IconTire className="w-5 h-5" />} title="Não foi possível carregar os pneus" description="Tente de novo em instantes." />
        ) : !d || d.rows.length === 0 ? (
          <EmptyState icon={<IconTire className="w-5 h-5" />} title="Nenhum pneu com estes filtros"
            description="Os pneus vêm da integração (Finatto/Sinersoft). Ajuste a busca ou a situação." />
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-surface-muted text-xs text-gray-500 uppercase">
              <tr>
                <th className="text-left px-4 py-2">Código</th>
                <th className="text-left px-4 py-2">Marca</th>
                <th className="text-left px-4 py-2 hidden md:table-cell">Modelo</th>
                <th className="text-left px-4 py-2 hidden lg:table-cell">Medida</th>
                <th className="text-left px-4 py-2">Vida</th>
                <th className="text-left px-4 py-2">Veículo</th>
                <th className="text-left px-4 py-2 hidden md:table-cell">Posição</th>
                <th className="text-right px-4 py-2">Sulco</th>
                <th className="text-right px-4 py-2 hidden lg:table-cell">KM acumulado</th>
                <th className="text-left px-4 py-2">Status</th>
                <th className="px-4 py-2 text-right">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {d.rows.map((t) => <Linha key={t.id} t={t} onMove={onMove} />)}
            </tbody>
          </table>
        )}
      </div>
      {d && d.total > 0 && (
        <div className="flex items-center justify-between text-xs text-gray-500">
          <span>Mostrando {(d.page - 1) * d.pageSize + 1} a {Math.min(d.page * d.pageSize, d.total)} de {d.total.toLocaleString("pt-BR")} registros</span>
          <div className="flex gap-1">
            <button className="btn-secondary px-3 py-1.5" disabled={page <= 1} onClick={() => set("pagina", String(page - 1))}>Anterior</button>
            <span className="px-2 py-1.5">{page} / {paginas}</span>
            <button className="btn-secondary px-3 py-1.5" disabled={page >= paginas} onClick={() => set("pagina", String(page + 1))}>Próxima</button>
          </div>
        </div>
      )}
    </div>
  );
}

function Linha({ t, onMove }: { t: TireViewDto; onMove: (p: MoveTirePreset) => void }) {
  const navigate = useNavigate();
  const { can } = useTireCapabilities();
  const [menu, setMenu] = useState(false);
  const [inspecionar, setInspecionar] = useState(false);
  return (
    <tr className="hover:bg-surface-muted cursor-pointer" onClick={() => navigate(`/frota/pneus/${t.id}`)}>
      <td className="px-4 py-3 font-mono text-gray-900">
        <Link to={`/frota/pneus/${t.id}`} onClick={(e) => e.stopPropagation()} className="hover:underline">{t.code}</Link>
      </td>
      <td className="px-4 py-3 text-gray-700">{t.brand ?? "—"}</td>
      <td className="px-4 py-3 text-gray-600 hidden md:table-cell">{t.model ?? "—"}</td>
      <td className="px-4 py-3 text-gray-600 hidden lg:table-cell whitespace-nowrap">{t.size ?? "—"}</td>
      <td className="px-4 py-3 text-gray-600">{t.life}</td>
      <td className="px-4 py-3 font-mono text-gray-700">
        {t.location ? (
          <Link to={`/frota/placa/${encodeURIComponent(t.location.plate)}?aba=pneus`} onClick={(e) => e.stopPropagation()} className="hover:underline">{t.location.plate}</Link>
        ) : t.status === "stock" ? <span className="font-sans text-gray-500">Estoque</span> : "—"}
      </td>
      <td className="px-4 py-3 text-gray-600 hidden md:table-cell">
        <span className="inline-flex items-center gap-1">
          {t.location?.positionLabel ?? "—"}
          {t.location?.origin === "snapshot" && <OriginBadge origin={t.source} />}
        </span>
      </td>
      <td className="px-4 py-3 text-right whitespace-nowrap">
        <span className={t.condition === "critical" ? "text-danger-600 font-medium" : t.condition === "attention" ? "text-warning-700 font-medium" : "text-gray-800"}>
          {fmtMm(t.treadMm)}
        </span>
      </td>
      <td className="px-4 py-3 text-right text-gray-600 hidden lg:table-cell whitespace-nowrap">{fmtKm(t.kmAccumulated)}</td>
      <td className="px-4 py-3">
        <div className="flex flex-wrap gap-1">
          <TireStatusBadge status={t.status} />
          {t.condition && t.condition !== "normal" && t.status !== "written_off" && <ConditionBadge condition={t.condition} />}
        </div>
      </td>
      <td className="px-4 py-3 text-right relative" onClick={(e) => e.stopPropagation()}>
        <button className="btn-ghost px-2 py-1" aria-label={`Ações do pneu ${t.code}`} onClick={() => setMenu((v) => !v)}>⋯</button>
        {menu && (
          <div className="absolute right-4 top-10 z-20 w-44 card py-1 text-left text-sm" onMouseLeave={() => setMenu(false)}>
            <Link className="block px-3 py-2 hover:bg-surface-muted" to={`/frota/pneus/${t.id}`}>Ver ficha</Link>
            {can("tires.move") && t.status !== "written_off" && (
              <button className="w-full text-left px-3 py-2 hover:bg-surface-muted" onClick={() => { setMenu(false); onMove({ tireId: t.id }); }}>Movimentar</button>
            )}
            {can("tires.inspect") && t.status !== "written_off" && (
              <button className="w-full text-left px-3 py-2 hover:bg-surface-muted" onClick={() => { setMenu(false); setInspecionar(true); }}>Inspecionar</button>
            )}
          </div>
        )}
        {inspecionar && (
          <InspectTireModal tireId={t.id} tireCode={t.code} plate={t.location?.plate} positionLabel={t.location?.positionLabel} onClose={() => setInspecionar(false)} />
        )}
      </td>
    </tr>
  );
}
